import { useEffect, useState } from 'react';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

type ProductionOperatorNumericKeyboardProps = {
  open: boolean;
  title: string;
  value: string;
  allowDecimal?: boolean;
  preserveLeadingZeros?: boolean;
  onConfirm: (value: string) => void;
  onClose: () => void;
};


const NUMBER_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export default function ProductionOperatorNumericKeyboard({
  open,
  title,
  value,
  allowDecimal = false,
  preserveLeadingZeros = false,
  onConfirm,
  onClose,
}: ProductionOperatorNumericKeyboardProps) {
  const [draftValue, setDraftValue] = useState(value);


  useEffect(() => {
    if (!open) return;

    setDraftValue(value);
  }, [open, value]);

  const appendDigit = (digit: string) => {
    setDraftValue((current) => {
      if (!preserveLeadingZeros && current === '0') return digit;
      return `${current}${digit}`;
    });
  };


  const appendDecimal = () => {
    if (!allowDecimal) return;

    setDraftValue((current) => {
      if (current.includes('.') || current.includes(',')) {
        return current;
      }

      return current ? `${current}.` : '0.';
    });
  };

  const removeLastCharacter = () => {
    setDraftValue((current) => current.slice(0, -1));
  };

  const keyButtonSx = {
    minHeight: 68,
    borderRadius: 2.5,
    borderColor: 'rgba(148,163,184,0.22)',
    bgcolor: 'rgba(30,41,59,0.72)',
    color: '#f8fafc',
    fontSize: '1.4rem',
    fontWeight: 900,
    '&:hover': {
      borderColor: 'rgba(56,189,248,0.48)',
      bgcolor: 'rgba(51,65,85,0.9)',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#0f172a',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.35}>
          <Typography sx={{ color: '#f8fafc', fontSize: '1.05rem', fontWeight: 900 }}>
            {title}
          </Typography>

          <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
            Teclado táctil PROA
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Box
            sx={{
              minHeight: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              borderRadius: 3,
              border: '1px solid rgba(56,189,248,0.22)',
              bgcolor: 'rgba(2,6,23,0.58)',
              px: 2,
              py: 1.25,
            }}
          >
            <Typography
              sx={{
                color: draftValue ? '#f8fafc' : '#64748b',
                fontSize: '2rem',
                fontWeight: 950,
                lineHeight: 1,
                overflowWrap: 'anywhere',
              }}
            >
              {draftValue || '—'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            {NUMBER_KEYS.map((key) => (
              <Button
                key={key}
                variant="outlined"
                onClick={() => appendDigit(key)}
                sx={keyButtonSx}
              >
                {key}
              </Button>
            ))}

            <Button
              variant="outlined"
              onClick={appendDecimal}
              disabled={!allowDecimal}
              sx={{
                ...keyButtonSx,
                opacity: allowDecimal ? 1 : 0.35,
              }}
            >
              ,
            </Button>

            <Button
              variant="outlined"
              onClick={() => appendDigit('0')}
              sx={keyButtonSx}
            >
              0
            </Button>

            <Button
              variant="outlined"
              aria-label="Borrar último carácter"
              onClick={removeLastCharacter}
              sx={keyButtonSx}
            >
              <BackspaceRoundedIcon />
            </Button>
          </Box>

          <Button
            variant="text"
            onClick={() => setDraftValue('')}
            sx={{
              alignSelf: 'center',
              color: '#94a3b8',
              fontWeight: 800,
            }}
          >
            Borrar todo
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
          }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => onConfirm(draftValue)}
          sx={{
            minWidth: 140,
            minHeight: 48,
            borderRadius: 2.5,
            bgcolor: '#38bdf8',
            color: '#020617',
            fontWeight: 950,
            '&:hover': {
              bgcolor: '#7dd3fc',
            },
          }}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}